import { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Search, MapPin, Loader2, Navigation, X } from "lucide-react";

// Fix default marker icon issue with Leaflet + bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface MapLocationPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialLocation?: { lat: number; lng: number } | null;
  onLocationSelect: (location: { lat: number; lng: number }) => void;
}

// Component to handle map click events
function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to fly to a location
function FlyToLocation({ position }: { position: [number, number] | null }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, 15, { duration: 1 });
    }
  }, [position, map]);
  
  return null;
}

export function MapLocationPicker({
  open,
  onOpenChange,
  initialLocation,
  onLocationSelect,
}: MapLocationPickerProps) {
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const [flyToPosition, setFlyToPosition] = useState<[number, number] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<{ display_name: string; lat: string; lon: string }>>([]); 
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  // Default center: India center (approximate)
  const defaultCenter: [number, number] = initialLocation 
    ? [initialLocation.lat, initialLocation.lng] 
    : [20.5937, 78.9629];
  const defaultZoom = initialLocation ? 15 : 5;
  
  useEffect(() => {
    if (open) {
      if (initialLocation) {
        setMarkerPosition([initialLocation.lat, initialLocation.lng]);
      } else {
        setMarkerPosition(null);
      }
      setSearchQuery("");
      setSearchResults([]);
      setShowResults(false);
      setFlyToPosition(null);
    }
  }, [open, initialLocation]);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);
    setShowResults(false);
  }, []);

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim() || query.trim().length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=5`,
        {
          headers: {
            "Accept-Language": "en",
          },
        }
      );
      const data = await response.json();
      setSearchResults(data);
      setShowResults(data.length > 0);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, 500);
  };

  const handleResultClick = (result: { display_name: string; lat: string; lon: string }) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setMarkerPosition([lat, lng]);
    setFlyToPosition([lat, lng]);
    setSearchQuery(result.display_name);
    setShowResults(false);
  };

  const handleConfirm = () => {
    if (markerPosition) {
      onLocationSelect({ lat: markerPosition[0], lng: markerPosition[1] });
      onOpenChange(false);
    }
  };

  const handleGetCurrentLocation = async () => {
    setIsGettingLocation(true);
    try {
      const { getCurrentLocation } = await import("@/services/geolocationService");
      const position = await getCurrentLocation(true, 10000);
      const newPos: [number, number] = [position.lat, position.lng];
      setMarkerPosition(newPos);
      setFlyToPosition(newPos);
    } catch (error) {
      console.error("Could not get current location:", error);
    } finally {
      setIsGettingLocation(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[95vw] w-full h-[85vh] flex flex-col p-0 gap-0">
        <AlertDialogHeader className="p-4 pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <AlertDialogTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-green-600" />
              Select Location
            </AlertDialogTitle>
            <AlertDialogCancel className="h-8 w-8 p-0 border-0">
              <X className="h-4 w-4" />
            </AlertDialogCancel>
          </div>
        </AlertDialogHeader>
        
        {/* Search Bar */}
        <div className="px-4 pb-2 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              placeholder="Search location..."
              className="pl-10 pr-10 h-10"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          
          {/* Search Results */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute left-4 right-4 z-[1000] bg-background border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleResultClick(result)}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary/80 border-b last:border-0 flex items-start gap-2"
                >
                  <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                  <span className="line-clamp-2">{result.display_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 relative mx-4 rounded-lg overflow-hidden border">
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            className="h-full w-full"
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onMapClick={handleMapClick} />
            {flyToPosition && <FlyToLocation position={flyToPosition} />}
            {markerPosition && (
              <Marker position={markerPosition} />
            )}
          </MapContainer>
          
          {/* Current Location Button on Map */}
          <Button
            variant="secondary"
            size="icon"
            className="absolute bottom-3 right-3 z-[400] h-10 w-10 rounded-full shadow-lg bg-white hover:bg-gray-100"
            onClick={handleGetCurrentLocation}
          >
            <Navigation className="h-4.5 w-4.5 text-green-600" />
          </Button>
        </div>

        {/* Selected Coordinates */}
        <div className="px-4 pt-2 flex-shrink-0">
          {isGettingLocation ? (
            <p className="text-sm text-center text-blue-600 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Acquiring GPS location…
            </p>
          ) : markerPosition ? (
            <p className="text-sm text-center text-muted-foreground">
              📍 {markerPosition[0].toFixed(6)}, {markerPosition[1].toFixed(6)}
            </p>
          ) : (
            <p className="text-sm text-center text-muted-foreground">
              Tap on the map or search to select a location
            </p>
          )}
        </div>

        {/* Footer */}
        <AlertDialogFooter className="p-4 pt-2 flex-shrink-0">
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!markerPosition}
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
          >
            <MapPin className="h-4 w-4 mr-2" />
            Confirm Location
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
